/**
 * Willtec PDP Custom Elements & Interactive Logic (Etapa 1)
 * Handles product gallery main image switching/thumbnails,
 * variant option selection & price/sku updates,
 * main product form quantity selector & AJAX submission to Minicart,
 * and shipping simulation (CEP).
 */
(() => {
  if (customElements.get('willtec-pdp-form')) return;

  class WilltecPdpGallery extends HTMLElement {
    connectedCallback() {
      this.mainImg = this.querySelector('[data-pdp-main-image]');
      this.thumbs = this.querySelectorAll('[data-pdp-thumb]');

      if (!this.mainImg || !this.thumbs.length) return;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', (e) => {
          e.preventDefault();
          const newSrc = thumb.getAttribute('data-full-src');
          if (newSrc && this.mainImg) {
            this.mainImg.src = newSrc;
            this.thumbs.forEach((t) => t.classList.remove('border-[#FE3B40]'));
            thumb.classList.add('border-[#FE3B40]');
          }
        });
      });
    }
  }

  class WilltecPdpForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.variantInput = this.form.querySelector('input[name="id"]');
      this.qtyInput = this.form.querySelector('input[name="quantity"]');
      this.qtyDisplay = this.querySelector('[data-pdp-qty-val]');
      this.minusBtn = this.querySelector('[data-pdp-qty-minus]');
      this.plusBtn = this.querySelector('[data-pdp-qty-plus]');
      this.buyNowBtn = this.form.querySelector('[data-action="buy-now"]') || this.form.querySelector('[type="submit"]');
      this.addToCartBtn = this.form.querySelector('[data-action="add-to-cart"]');
      this.errorContainer = this.querySelector('[data-pdp-error]');

      this.skuDisplay = document.querySelector('[data-pdp-sku]');
      this.priceDisplay = document.querySelector('[data-pdp-price]');
      this.installmentsWrapper = document.querySelector('[data-pdp-installments-wrapper]');
      this.saleRow = document.querySelector('[data-pdp-sale-row]');
      this.comparePriceDisplay = document.querySelector('[data-pdp-compare-price]');
      this.discountPercentEl = document.querySelector('[data-pdp-discount-percent]');

      this.bindQuantity();
      this.bindVariantSelector();
      this.bindSubmit();
    }

    getQuantity() {
      const el = (this.qtyDisplay && this.qtyDisplay.tagName === 'INPUT') ? this.qtyDisplay : (this.qtyInput || this.qtyDisplay);
      if (!el) return 1;
      const raw = el.tagName === 'INPUT' ? el.value : el.textContent;
      const val = parseInt(raw, 10);
      return isNaN(val) || val < 1 ? 1 : val;
    }

    setQuantity(num, format = true) {
      const safeVal = Math.max(1, parseInt(num, 10) || 1);
      const displayVal = format && safeVal < 10 ? `0${safeVal}` : `${safeVal}`;

      if (this.qtyInput) {
        this.qtyInput.value = safeVal;
      }

      if (this.qtyDisplay) {
        if (this.qtyDisplay.tagName === 'INPUT') {
          this.qtyDisplay.value = format ? displayVal : safeVal;
        } else {
          this.qtyDisplay.textContent = displayVal;
        }
      }

      const notifyEl = this.qtyInput || this.qtyDisplay;
      if (notifyEl) {
        notifyEl.dispatchEvent(new Event('input', { bubbles: true }));
        notifyEl.dispatchEvent(new Event('change', { bubbles: true }));
      }

      return safeVal;
    }

    bindQuantity() {
      if (!this.qtyInput && !this.qtyDisplay) return;

      if (this.minusBtn) {
        this.minusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const current = this.getQuantity();
          if (current > 1) {
            this.setQuantity(current - 1);
          }
        });
      }

      if (this.plusBtn) {
        this.plusBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const current = this.getQuantity();
          this.setQuantity(current + 1);
        });
      }

      const inputEl = (this.qtyDisplay && this.qtyDisplay.tagName === 'INPUT')
        ? this.qtyDisplay
        : (this.qtyInput && this.qtyInput.type !== 'hidden' ? this.qtyInput : null);

      if (inputEl) {
        // Automatically select text on focus for quick typing
        inputEl.addEventListener('focus', () => {
          inputEl.select();
        });

        // Sync without altering cursor during input
        inputEl.addEventListener('input', () => {
          const val = parseInt(inputEl.value, 10);
          if (!isNaN(val) && val >= 1) {
            if (this.qtyInput && this.qtyInput !== inputEl) {
              this.qtyInput.value = val;
            }
          }
        });

        const commitValue = () => {
          const current = parseInt(inputEl.value, 10);
          if (isNaN(current) || current < 1) {
            this.setQuantity(1);
          } else {
            this.setQuantity(current);
          }
        };

        inputEl.addEventListener('blur', commitValue);
        inputEl.addEventListener('change', commitValue);

        inputEl.addEventListener('keydown', (e) => {
          // Block non-numeric characters e, +, -, .
          if (['e', 'E', '+', '-', '.'].includes(e.key)) {
            e.preventDefault();
            return;
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            commitValue();
            if (this.buyNowBtn && !this.buyNowBtn.disabled) {
              this.buyNowBtn.click();
            }
          }
        });
      }
    }

    bindVariantSelector() {
      const variantJsonEl = this.querySelector('[data-pdp-variants-json]');
      if (!variantJsonEl) return;

      try {
        this.variants = JSON.parse(variantJsonEl.textContent);
      } catch (e) {
        return;
      }

      const optionInputs = this.querySelectorAll('[data-pdp-option]');
      optionInputs.forEach((input) => {
        input.addEventListener('change', () => this.updateVariantFromOptions());
      });
    }

    updateVariantFromOptions() {
      if (!this.variants || !this.variants.length) return;

      const selectedOptions = Array.from(this.querySelectorAll('[data-pdp-option]')).map((el) => el.value);

      const matchedVariant = this.variants.find((v) => {
        return v.options.every((opt, idx) => opt === selectedOptions[idx]);
      });

      if (matchedVariant) {
        if (this.variantInput) {
          this.variantInput.value = matchedVariant.id;
          this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.skuDisplay) {
          this.skuDisplay.textContent = matchedVariant.sku ? matchedVariant.sku : '';
          const skuWrapper = this.skuDisplay.closest('[data-pdp-sku-wrapper]');
          if (skuWrapper) {
            if (matchedVariant.sku) {
              skuWrapper.classList.remove('hidden');
            } else {
              skuWrapper.classList.add('hidden');
            }
          }
        }

        const pixDiscount = this.installmentsWrapper ? (parseFloat(this.installmentsWrapper.dataset.pixDiscount) || 0) : 0;
        const boletoDiscount = this.installmentsWrapper ? (parseFloat(this.installmentsWrapper.dataset.boletoDiscount) || 0) : 0;

        let maxDiscount = 0;
        let discountMethods = '';
        if (pixDiscount > 0 && boletoDiscount > 0) {
          maxDiscount = pixDiscount;
          discountMethods = 'Pix ou boleto';
        } else if (pixDiscount > 0) {
          maxDiscount = pixDiscount;
          discountMethods = 'Pix';
        } else if (boletoDiscount > 0) {
          maxDiscount = boletoDiscount;
          discountMethods = 'boleto';
        }

        let displayPrice = matchedVariant.price;
        if (maxDiscount > 0) {
          displayPrice = Math.round(matchedVariant.price * (1 - maxDiscount / 100));
        }

        if (this.priceDisplay) {
          this.priceDisplay.textContent = this.formatMoney(displayPrice);
        }

        if (this.installmentsWrapper) {
          const price = matchedVariant.price;
          const minInstallmentValue = parseInt(this.installmentsWrapper.dataset.minInstallmentValue, 10) || 10000;
          const maxInstallments = parseInt(this.installmentsWrapper.dataset.maxInstallments, 10) || 3;
          const possible = Math.floor(price / minInstallmentValue);
          const count = Math.min(possible, maxInstallments);

          let html = '';
          if (maxDiscount > 0) {
            html += `já com <strong class="font-bold text-black">${maxDiscount}% de desconto</strong> à vista no ${discountMethods}<br>`;
            html += `ou <strong class="font-bold text-black" data-pdp-card-price>${this.formatMoney(price)}</strong>`;
            if (count > 1) {
              const amount = Math.floor(price / count);
              html += ` em até <strong class="font-bold text-black">${count}x de ${this.formatMoney(amount)}</strong> sem juros`;
            }
            html += ` no cartão`;
          } else {
            html += `<strong class="font-bold text-black" data-pdp-card-price>${this.formatMoney(price)}</strong>`;
            if (count > 1) {
              const amount = Math.floor(price / count);
              html += ` em até <strong class="font-bold text-black">${count}x de ${this.formatMoney(amount)}</strong> sem juros`;
            }
            html += ` no cartão`;
          }

          this.installmentsWrapper.innerHTML = html;
          this.installmentsWrapper.classList.remove('hidden');
        }

        const paymentModal = document.getElementById('WilltecPaymentMethodsModal');
        if (paymentModal && typeof paymentModal.updatePrice === 'function') {
          paymentModal.updatePrice(matchedVariant.price);
        }

        const hasDiscount = matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price;

        if (this.saleRow) {
          if (hasDiscount) {
            if (this.comparePriceDisplay) {
              this.comparePriceDisplay.textContent = this.formatMoney(matchedVariant.compare_at_price);
            }
            if (this.discountPercentEl) {
              const discountPercent = Math.round(((matchedVariant.compare_at_price - matchedVariant.price) * 100) / matchedVariant.compare_at_price);
              this.discountPercentEl.textContent = `${discountPercent}%`;
            }
            this.saleRow.classList.remove('hidden');
          } else {
            this.saleRow.classList.add('hidden');
          }
        }

        if (matchedVariant.featured_media && matchedVariant.featured_media.id) {
          document.dispatchEvent(
            new CustomEvent('willtec:gallery:select-media', {
              detail: { mediaId: matchedVariant.featured_media.id }
            })
          );
        }

        document.dispatchEvent(
          new CustomEvent('willtec:variant:change', {
            detail: {
              variant: matchedVariant,
              variantId: matchedVariant.id,
              price: matchedVariant.price,
              available: matchedVariant.available,
              featuredMedia: matchedVariant.featured_media
            }
          })
        );

        const stdVariantEvent = new CustomEvent('variant:change', {
          bubbles: true,
          detail: { variant: matchedVariant }
        });
        if (this.form) this.form.dispatchEvent(stdVariantEvent);
        document.dispatchEvent(stdVariantEvent);

        if (this.buyNowBtn) {
          if (matchedVariant.available) {
            this.buyNowBtn.disabled = false;
            this.buyNowBtn.innerHTML = '<span>Comprar agora</span>';
          } else {
            this.buyNowBtn.disabled = true;
            this.buyNowBtn.innerHTML = '<span>Indisponível</span>';
          }
        }

        if (this.addToCartBtn) {
          if (matchedVariant.available) {
            this.addToCartBtn.disabled = false;
            this.addToCartBtn.innerHTML = '<span>Adicionar ao carrinho</span>';
          } else {
            this.addToCartBtn.disabled = true;
            this.addToCartBtn.innerHTML = '<span>Indisponível</span>';
          }
        }
      }
    }

    formatMoney(cents) {
      return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    }

    bindSubmit() {
      if (this.addToCartBtn) {
        this.addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAddToCart();
        });
      }

      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleBuyNow();
      });
    }

    async handleAddToCart() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      if (this.errorContainer) {
        this.errorContainer.classList.add('hidden');
        this.errorContainer.textContent = '';
      }

      const originalCartHtml = this.addToCartBtn ? this.addToCartBtn.innerHTML : '';
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.setAttribute('aria-busy', 'true');
        this.addToCartBtn.innerHTML = '<span class="animate-pulse">Adicionando...</span>';
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = true;
      }

      // Ensure valid quantity is committed before creating FormData
      const currentQty = this.getQuantity();
      this.setQuantity(currentQty);

      const formData = new FormData(this.form);
      formData.append('sections', 'willtec-cart-drawer,header');
      formData.append('sections_url', window.location.pathname);

      const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';

      try {
        const response = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        const data = await response.json();

        if (response.ok) {
          if (data.sections) {
            const drawerEl = document.getElementById('WilltecCartDrawer');
            if (drawerEl && data.sections['willtec-cart-drawer']) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(data.sections['willtec-cart-drawer'], 'text/html');
              const newDrawerContent = doc.getElementById('WilltecCartDrawer');
              if (newDrawerContent) {
                drawerEl.innerHTML = newDrawerContent.innerHTML;
                if (typeof drawerEl.bindEvents === 'function') {
                  drawerEl.bindEvents();
                }
              }
            }

            if (data.sections['header']) {
              const parser = new DOMParser();
              const doc = parser.parseFromString(data.sections['header'], 'text/html');
              const newHeaderCounts = doc.querySelectorAll('.willtec-cart-count');
              document.querySelectorAll('.willtec-cart-count').forEach((badge, idx) => {
                if (newHeaderCounts[idx]) badge.innerHTML = newHeaderCounts[idx].innerHTML;
              });
            }
          }

          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:open'));

          // Reset quantity to 1
          this.setQuantity(1);
        } else {
          const errorMsg = data.description || data.message || 'Não foi possível adicionar o produto ao carrinho.';
          if (this.errorContainer) {
            this.errorContainer.textContent = errorMsg;
            this.errorContainer.classList.remove('hidden');
          }
        }
      } catch (err) {
        console.error('Willtec PDP Form Error:', err);
        if (this.errorContainer) {
          this.errorContainer.textContent = 'Erro de conexão com o servidor. Tente novamente.';
          this.errorContainer.classList.remove('hidden');
        }
      } finally {
        this.isSubmitting = false;
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.removeAttribute('aria-busy');
          this.addToCartBtn.innerHTML = originalCartHtml;
        }
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = false;
        }
      }
    }

    async handleBuyNow() {
      if (this.isSubmitting) return;

      if (this.errorContainer) {
        this.errorContainer.classList.add('hidden');
        this.errorContainer.textContent = '';
      }

      const variantId = this.variantInput ? this.variantInput.value : '';
      const currentQty = this.getQuantity();
      this.setQuantity(currentQty);

      if (!variantId) {
        if (this.errorContainer) {
          this.errorContainer.textContent = 'Por favor, selecione uma opção válida.';
          this.errorContainer.classList.remove('hidden');
        }
        return;
      }

      // Buy Now on Product Page: always open modal before proceeding to checkout
      if (window.FiscalCheckoutGuard) {
        window.FiscalCheckoutGuard.openModal({
          mode: 'checkout',
          triggerEl: this.buyNowBtn,
          onConfirm: () => {
            this.executeBuyNow();
          }
        });
        return;
      }

      this.executeBuyNow();
    }

    async executeBuyNow() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      const originalBuyHtml = this.buyNowBtn ? this.buyNowBtn.innerHTML : '';
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.setAttribute('aria-busy', 'true');
        this.buyNowBtn.innerHTML = '<span class="animate-pulse">Indo para o checkout...</span>';
      }
      if (this.addToCartBtn) {
        this.addToCartBtn.disabled = true;
      }

      const rootUrl = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
      const variantId = this.variantInput ? this.variantInput.value : '';
      const currentQty = this.getQuantity();
      this.setQuantity(currentQty);
      const quantity = currentQty;

      try {
        // 1. Limpa o carrinho atual para que o checkout contenha APENAS este produto
        await fetch(`${rootUrl}cart/clear.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' }
        });

        // 2. Adiciona o produto selecionado com FormData (preserva variantes, quantidade, custom properties e selling plans)
        const formData = new FormData(this.form);
        const addResponse = await fetch(`${rootUrl}cart/add.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json'
          },
          body: formData
        });

        if (!addResponse.ok) {
          throw new Error('Falha ao adicionar o produto.');
        }

        // 3. Aplica e confirma o estado fiscal no carrinho
        if (window.FiscalCheckoutGuard) {
          await window.FiscalCheckoutGuard.syncFiscalStateToCart();
        }

        // 4. Direciona imediatamente para o checkout
        window.location.href = `${rootUrl}checkout`;
      } catch (err) {
        console.error('Willtec PDP Buy Now Error:', err);
        if (this.errorContainer) {
          this.errorContainer.textContent = 'Não foi possível concluir a compra imediata. Tente novamente.';
          this.errorContainer.classList.remove('hidden');
        }
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = false;
          this.buyNowBtn.removeAttribute('aria-busy');
          this.buyNowBtn.innerHTML = originalBuyHtml;
        }
        if (this.addToCartBtn) {
          this.addToCartBtn.disabled = false;
        }
        this.isSubmitting = false;
      }
    }
  }

  class WilltecPdpShipping extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-shipping-cep]');
      this.calcBtn = this.querySelector('[data-shipping-calc]');
      this.resultContainer = this.querySelector('[data-shipping-results]');

      if (!this.calcBtn || !this.input) return;

      this.calcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const cep = this.input.value.replace(/\D/g, '');
        if (cep.length !== 8) {
          alert('Por favor, informe um CEP válido com 8 dígitos.');
          return;
        }

        if (this.resultContainer) {
          this.resultContainer.innerHTML = '<span class="text-xs font-sans text-zinc-500 animate-pulse">Simulando frete...</span>';
          this.resultContainer.classList.remove('hidden');

          setTimeout(() => {
            this.resultContainer.innerHTML = `
            <div class="flex flex-col gap-2 pt-3 border-t border-zinc-200 text-xs font-sans">
              <div class="flex items-center justify-between">
                <span class="font-bold text-black">SEDEX (2 a 3 dias úteis)</span>
                <span class="font-bold text-black">R$ 18,90</span>
              </div>
              <div class="flex items-center justify-between">
                <span class="font-bold text-black">PAC (5 a 8 dias úteis)</span>
                <span class="font-bold text-[#FE3B40]">Grátis</span>
              </div>
            </div>
          `;
          }, 500);
        }
      });
    }
  }

  class WilltecPdpAccordion {
    static init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-pdp-accordion-trigger]');
        if (!trigger) return;

        const accordion = trigger.closest('[data-pdp-accordion]');
        if (!accordion) return;

        const panel = accordion.querySelector('[data-pdp-accordion-panel]');
        const icon = trigger.querySelector('[data-pdp-accordion-icon]');
        if (!panel) return;

        const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          trigger.setAttribute('aria-expanded', 'false');
          panel.classList.add('hidden');
          if (icon) icon.classList.remove('rotate-180');
        } else {
          trigger.setAttribute('aria-expanded', 'true');
          panel.classList.remove('hidden');
          if (icon) icon.classList.add('rotate-180');
          window.dispatchEvent(new Event('resize'));
        }
      });

      WilltecPdpAccordion.initDescriptionTruncation();
    }

    static initDescriptionTruncation() {
      const descContent = document.querySelector('[data-pdp-description-content]');
      const descFade = document.querySelector('[data-pdp-description-fade]');
      const descToggle = document.querySelector('[data-pdp-description-toggle]');

      if (!descContent || !descToggle || !descFade) return;

      // Check if scrollHeight > 540px to decide if truncation CTA is needed
      const maxHeight = 540;
      if (descContent.scrollHeight > maxHeight) {
        descFade.classList.remove('hidden');
        descFade.classList.add('flex');
      } else {
        descContent.classList.remove('max-h-[540px]');
      }

      descToggle.addEventListener('click', (e) => {
        e.preventDefault();
        const isTruncated = descContent.classList.contains('max-h-[540px]');
        const toggleText = descToggle.querySelector('[data-pdp-description-toggle-text]');
        const toggleIcon = descToggle.querySelector('[data-pdp-description-toggle-icon]');

        if (isTruncated) {
          descContent.classList.remove('max-h-[540px]');
          descFade.classList.remove('-mt-16', 'bg-gradient-to-t', 'from-white', 'via-white/90', 'to-transparent');
          if (toggleText) toggleText.textContent = 'Ver menos';
          if (toggleIcon) toggleIcon.classList.add('rotate-180');
        } else {
          descContent.classList.add('max-h-[540px]');
          descFade.classList.add('-mt-16', 'bg-gradient-to-t', 'from-white', 'via-white/90', 'to-transparent');
          if (toggleText) toggleText.textContent = 'Ver detalhes';
          if (toggleIcon) toggleIcon.classList.remove('rotate-180');
        }
      });
    }
  }

  /**
   * Reviews Widget Empty State Handler
   * Injeta empty state SOMENTE quando a div com a classe especificada
   * contém <= 1 elemento (produto sem avaliações).
   *
   * Classe: jm-stack jm-stack--space-600 jm-review-widget__header jm-review-widget-sidebar-header__sidebar
   */
  function initReviewsEmptyState() {
    const selector = '.jm-stack.jm-stack--space-600.jm-review-widget__header.jm-review-widget-sidebar-header__sidebar, .jm-review-widget-sidebar-header__sidebar';
    let hasExecuted = false;
    let observer = null;
    let interval = null;

    function cleanup() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    }

    function checkAndApply() {
      if (hasExecuted) return;

      const sidebar = document.querySelector(selector);
      if (!sidebar) return;

      // Executa apenas uma vez: desconecta o observer e limpa o timer imediatamente
      hasExecuted = true;
      cleanup();

      const existingEmptyState = sidebar.querySelector('[data-willtec-empty-reviews]');
      const actualChildrenCount = existingEmptyState ? (sidebar.children.length - 1) : sidebar.children.length;

      // Condição estrita: quando o produto não tem reviews, contém <= 1 elemento
      if (actualChildrenCount <= 1) {
        if (!existingEmptyState) {
          // Oculta elemento residual existente
          Array.from(sidebar.children).forEach((child) => {
            child.style.display = 'none';
          });

          const emptyCard = document.createElement('div');
          emptyCard.style.marginTop = 0;
          emptyCard.setAttribute('data-willtec-empty-reviews', 'true');
          emptyCard.className = 'willtec-reviews-empty-state flex flex-col items-center justify-center p-6 md:p-8 text-center border border-[#DDDDDD] rounded-[12px] bg-[#FBFBFB] w-full min-h-[200px] h-full';
          emptyCard.innerHTML = `
            <div class="flex items-center justify-center gap-1.5 text-[#CBD5E1] mb-3">
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
              <svg class="w-5 h-5 fill-current" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
            </div>
            <h4 class="font-heading font-normal text-[20px] md:text-[24px] leading-tight text-[#202020] uppercase tracking-wide mb-1">
              Ainda não há avaliações para este produto
            </h4>
            <p class="font-sans text-[12px] md:text-[13px] leading-relaxed text-[#64748B] mb-4 max-w-[280px]">
              Seja o primeiro a avaliar e compartilhe sua experiência com outros clientes.
            </p>
          `;

          sidebar.appendChild(emptyCard);
        }
      }
    }

    checkAndApply();

    if (!hasExecuted) {
      // Observa injeção assíncrona do Judge.me até executar uma única vez
      const target = document.querySelector('[data-pdp-reviews-wrapper]') || document.body;
      observer = new MutationObserver(() => {
        checkAndApply();
      });
      observer.observe(target, { childList: true, subtree: true });

      // Polling de garantia para conexões mais lentas (máximo 5 segundos)
      let attempts = 0;
      interval = setInterval(() => {
        attempts++;
        checkAndApply();
        if (attempts >= 10 || hasExecuted) {
          cleanup();
        }
      }, 500);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      WilltecPdpAccordion.init();
      initReviewsEmptyState();
    });
  } else {
    WilltecPdpAccordion.init();
    initReviewsEmptyState();
  }

  if (!customElements.get('willtec-pdp-gallery')) {
    customElements.define('willtec-pdp-gallery', WilltecPdpGallery);
  }
  if (!customElements.get('willtec-pdp-form')) {
    customElements.define('willtec-pdp-form', WilltecPdpForm);
  }
  if (!customElements.get('willtec-pdp-shipping')) {
    customElements.define('willtec-pdp-shipping', WilltecPdpShipping);
  }
})();

